import { PhoneCall, UtensilsCrossed, Home as HomeIcon, BookOpen, MapPin } from "lucide-react";
import { PageHero, CTABand, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const HELPLINES = [
  { icon: PhoneCall, name: "988 Suicide & Crisis Lifeline", detail: "Call or text 988 — free, confidential, 24/7 support.", href: "tel:988" },
  { icon: PhoneCall, name: "National Domestic Violence Hotline", detail: "1-800-799-7233 — 24/7 support and safety planning.", href: "tel:18007997233" },
  { icon: HomeIcon, name: "211 Community Services", detail: "Dial 211 for local housing, food, and utility assistance.", href: "tel:211" },
  { icon: UtensilsCrossed, name: "Feeding America Food Bank Finder", detail: "Locate free groceries and meal programs near you.", href: "https://www.feedingamerica.org/find-your-local-foodbank" },
];

const LOCAL_AGENCIES = [
  {
    name: "Beacon Light Behavioral Health Services",
    desc: "Outpatient mental health care: psychiatric evaluations, medication management, individual, group, and family therapy, plus peer support.",
    phone: "814-723-1832",
    tel: "tel:18147231832",
    web: "https://www.beacon-light.org",
    addr: "1885 Market Street, Warren, PA 16365",
  },
  {
    name: "Family Services of Warren County",
    desc: "Behavioral health services, substance abuse counseling, and support groups for individuals and families.",
    phone: "814-723-1330",
    tel: "tel:18147231330",
    web: "https://fswc.org",
    addr: "589 Hospital Drive, Suite E, Warren, PA 16365",
  },
  {
    name: "Warren-Forest Human Services",
    desc: "County mental health and crisis intake, plus children and youth services. For emergencies after 4:30 PM or on weekends, call 911.",
    phone: "814-726-2100",
    tel: "tel:18147262100",
    web: "https://www.wfcaa.org",
    addr: "Warren, PA",
  },
  {
    name: "A Safe Place",
    desc: "Free, confidential domestic violence and sexual assault services for Warren and Forest Counties — 24-hour crisis line, safety planning, legal advocacy, and emergency shelter.",
    phone: "1-800-338-3460 (24 hrs)",
    tel: "tel:18003383460",
    web: "https://asafeplacewarren.org",
    addr: "210 North Drive, Suite C, Warren, PA 16365",
  },
  {
    name: "Warren-Forest Counties Economic Opportunity Council (EOC)",
    desc: "Housing and social services, including transitional housing for survivors referred by A Safe Place.",
    phone: "814-726-2400",
    tel: "tel:18147262400",
    web: "https://www.wfcaa.org",
    addr: "Faith Inn, 225 Pennsylvania Ave. West, Warren, PA 16365",
  },
  {
    name: "Women's Care Center",
    desc: "Free, confidential support for women and families facing planned or unplanned pregnancies — pregnancy testing, ultrasounds by appointment, material assistance, counseling, and agency referrals.",
    phone: "814-723-4357",
    tel: "tel:18147234357",
    web: "https://www.wccerie.org",
    addr: "203 West 3rd Avenue, Warren, PA 16365",
  },
];

const FAQS = [
  { q: "Who can join the program?", a: "Any woman who has experienced trauma, loss, or instability and is ready to begin a healing journey. There are no faith requirements to apply — our program is faith-rooted but open-armed." },
  { q: "Does the program cost anything?", a: "No. Thanks to donors and volunteers, participation, meals, garden training, and coaching are provided at no cost to participants." },
  { q: "Do I have to commit to all three years?", a: "The three-year journey is the full vision, but you move at your own pace. Many women begin with a single dinner or garden day before deciding to apply." },
  { q: "Is my information kept private?", a: "Yes. Applications, referrals, and conversations are held in strict confidence. See our Privacy Policy for details." },
  { q: "Can professionals refer clients?", a: "Absolutely — therapists, social workers, pastors, and case managers can use our Professional Referrals page to begin a coordinated-care conversation." },
];

export default function Resources() {
  useTitle("Resources | Bee Courageous @ Garden Grub");
  return (
    <>
      <PageHero
        eyebrow="Resources"
        lines={["Help is closer", "than you think."]}
        lede="Immediate help lines, community services, answers to common questions, and encouragement for the road."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <SectionHead eyebrow="Immediate Help" title="If you need support right now" text="These national services are free, confidential, and available around the clock." />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:gap-8">
          {HELPLINES.map((h, i) => (
            <Reveal key={h.name} delay={0.06 * i}>
              <a href={h.href} className="card-soft group flex h-full items-start gap-5" data-testid={`helpline-${i}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-garden-soft text-garden">
                  <h.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-serif text-xl font-semibold text-charcoal group-hover:text-wing">{h.name}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-charcoal/70">{h.detail}</span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <p className="rounded-2xl border border-pollen/40 bg-pollen-soft p-5 text-sm font-semibold text-charcoal/80" data-testid="emergency-note">
            If you or someone else is in immediate danger, please call 911 first.
          </p>
        </Reveal>
      </section>

      {/* Local Warren, PA agencies */}
      <section className="bg-garden-soft/60 py-20 lg:py-28" data-testid="local-agencies">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHead
            eyebrow="Local Help in Warren, PA"
            title="Trusted agencies in our own backyard"
            text="We walk alongside these Warren County organizations and can help you connect with the right one."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {LOCAL_AGENCIES.map((a, i) => (
              <Reveal key={a.name} delay={0.06 * i}>
                <article className="card-soft flex h-full flex-col" data-testid={`agency-${i}`}>
                  <h3 className="font-serif text-xl font-semibold text-garden">{a.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal/70">{a.desc}</p>
                  <div className="mt-5 space-y-2 text-sm">
                    <a href={a.tel} className="flex items-center gap-2 font-bold text-wing hover:underline" data-testid={`agency-phone-${i}`}>
                      <PhoneCall className="h-4 w-4 shrink-0" aria-hidden="true" /> {a.phone}
                    </a>
                    <a href={a.web} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-charcoal/70 hover:text-wing" data-testid={`agency-web-${i}`}>
                      <BookOpen className="h-4 w-4 shrink-0" aria-hidden="true" /> Visit website
                    </a>
                    <p className="flex items-center gap-2 text-charcoal/60">
                      <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" /> {a.addr}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-wing-soft/50 py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <SectionHead center eyebrow="Common Questions" title="Wondering how it works?" />
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <details className="group rounded-2xl border border-wing/15 bg-white p-5 shadow-sm" data-testid={`faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-serif text-lg font-semibold text-charcoal [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span className="text-2xl font-light text-wing transition-transform duration-300 group-open:rotate-45" aria-hidden="true">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70 sm:text-base">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-24">
        <Reveal>
          <BookOpen className="mx-auto mb-5 h-9 w-9 text-pollen-dark" aria-hidden="true" />
          <blockquote className="font-serif text-2xl font-medium italic leading-snug text-charcoal sm:text-3xl">
            "Come to me, all you who are weary and burdened, and I will give you rest."
          </blockquote>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.25em] text-wing">Matthew 11:28</p>
        </Reveal>
      </section>

      <CTABand
        title="Still have questions? We would love to talk."
        text="Reach out — a real person from our community will answer with warmth."
        primaryLabel="Contact Us"
        primaryTo="/contact"
      />
    </>
  );
}
